import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  linkedSignal,
  QueryList,
  signal,
  ViewChildren,
  viewChildren,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule, ClrDatagrid, ClrDatagridColumn, ClrDatagridSortOrder, ClrModal } from '@clr/angular';
import { DAYS_OF_WEEK } from 'angular-calendar';
import dayjs from 'dayjs';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, firstValueFrom, map, of, startWith, switchMap, tap } from 'rxjs';
import { DeselectSingleSelectedDirective } from 'src/app/directives/deselect-single-selected.directive';
import { Table } from 'src/app/models/ui/table';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { OverlayService } from 'src/app/services/Overlay/overlay.service';
import { WartungsPlanNode } from '../_components/_overlays/wartungsverwaltung/wartung.types';
import { HistorieTreeComponent } from '../_components/detailview/historie-tree/historie-tree.component';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { MediaApiFunc } from 'src/app/services/MediaApiFunc/abstract-media-api-func';
import { WartungMediaApiFuncService } from 'src/app/services/MediaApiFunc/wartung-media-api-func.service';
import {
  MediaViewModalComponent,
  TitleAndFileNames,
} from '../_components/_shared/mediaviewmodal/mediaviewmodal.component';
import { ExpandButtonsComponent } from '../_components/_shared/expand-buttons/expand-buttons.component';
import { ExpandedAnsicht, ExpandedStateStore } from '../_components/_shared/expand-buttons/expanded.store';
import { MultiProtokollModel } from './wartungdialog/wartung-protokoll.types';
import { WartungApiService } from 'src/app/services/APIService/wartung.api.service';
import { ColumnFilterComponent } from '../_components/filter/column-filter/column-filter.component';
import { cached } from 'src/app/misc/operators';
import { ColumnHideManService } from 'src/app/services/Shared/colhidemanager.service';
import { FILTER_TABLE_STORE, provideFilterStore } from 'src/app/shared/stores/filter-table.store';
import { FilterService } from '../_components/filter/filter.service';
import { APIService } from 'src/app/services/APIService/api.service';
import { SetupConfig } from 'src/app/services/_offline/rep-setup.service';
import { AuthService } from 'src/app/services/Auth/auth.service';
import { getTokenRight } from 'src/app/misc/functions';
import { ReplikatWartungenSetupComponent } from '../_components/_modals/replikat-wartungen-setup/replikat-wartungen-setup.component';
import { ReplikatProgressComponent } from '../_components/_modals/replikat-progress/replikat-progress.component';
import { ConfirmService } from 'src/app/services/confirm.service';
import { DatalistInputComponent } from '../_components/_shared/datalist-input/datalist-input.component';
import { ExcelService } from 'src/app/services/Shared/excel.service';
// import {jsPDF,TableConfig} from 'jspdf';

const COL_NAME = 'Wartung';

@Component({
  selector: 'app-wartung',
  templateUrl: './wartung.component.html',
  styleUrls: ['./wartung.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    MediaViewModalComponent,
    ExpandButtonsComponent,
    HistorieTreeComponent,
    ColumnFilterComponent,
    ReplikatWartungenSetupComponent,
    ReplikatProgressComponent,
    DatalistInputComponent,
    DeselectSingleSelectedDirective,
    TablePrettyPrintPipe,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    HideIDColumnsPipe,
  ],
  providers: [
    { provide: MediaApiFunc, useClass: WartungMediaApiFuncService },
    provideFilterStore({ storage: 'wartungen' }),
    FilterService,
    ExpandedStateStore,
  ],
})
export class WartungComponent implements OnDestroy {
  private readonly excelService = inject(ExcelService);

  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  protected readonly media = inject(WartungMediaApiFuncService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  private readonly basicStore = inject(BasicStoreService);
  private readonly overlayService = inject(OverlayService);
  private readonly colHideService = inject(ColumnHideManService);
  private readonly toastr = inject(ToastrService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly api = inject(WartungApiService);
  public  readonly authService = inject(AuthService);
  private readonly confirmService = inject(ConfirmService);
  protected readonly isRep = inject(APIService).isRep;

  private readonly columnFilters = viewChildren(ColumnFilterComponent);
  protected readonly formatDBName = localStorage.getItem('FormatCurDB').replace('_', ' ');
  @ViewChildren(ClrDatagridColumn) columns: QueryList<ClrDatagridColumn>;

  private readonly dataRefresh = new Subject<void>();

  protected readonly wartungen$: Observable<Table<WartungsPlanNode>>;
  protected readonly filteredRows = this.filterStore.filteredRows;

  protected readonly bemerkungen$ = this.api.getWartungsBemerkungenList();

  protected readonly selectedNode = signal<WartungsPlanNode>(null);
  protected readonly selectedNodes = signal<WartungsPlanNode[]>([]);

  protected readonly isLoading = signal(false);
  protected readonly isMultiMode = signal(false);

  protected readonly datumModel = signal(BackendLocaleDatePipe.now);
  protected readonly bemerkungModel = signal<string>(null);

  protected readonly expandedState = signal<ExpandedAnsicht>(null);
  protected readonly historieDetailSize = computed(() => {
    switch (this.expandedState()) {
      case 'full':
        return 'full-screen';
      case 'half':
        return 'lg';
      case 'third':
        return 'md';
    }
  });
  protected readonly isHistorieInitialized = linkedSignal(() => (this.selectedNode(), false));
  protected readonly wartungsMedia = signal<TitleAndFileNames[]>([]);

  protected readonly tableIconColumnValues = signal<Record<string, string>>(undefined);
  protected readonly tableUniqueColumnValues = signal<Record<string, Set<unknown>>>(undefined);
  protected readonly hides = signal(
    this.colHideService.initColHide(['strbez', 'wid', 'mid', 'pid', 'icon', 'revbild', 'archiv_mid', 'typ'], COL_NAME)
  );

  protected readonly allowRepErstellen: boolean;
  protected readonly allowRepZurückspielen: boolean;

  protected readonly isReplikatProgressOpen = signal(false);
  protected readonly isUserReplikatModalOpen = signal(false);
  protected readonly setupConfig = signal<SetupConfig>(undefined);

  private readonly grid = viewChild(ClrDatagrid);

  constructor() {
    const { wartungen = null } = this.basicStore.getComponentStore('wartung') ?? {};
    this.wartungen$ = this.dataRefresh.pipe(
      startWith(wartungen === null),
      switchMap(reload =>
        reload === false ?
          of(wartungen)
        : this.api.getWartungsPlan('table').pipe(
            tap(() => this.isLoading.set(true)),
            map(({ columns, rows }) => ({
              columns,
              rows: findTermine(rows),
            })),
            tap(wartungen => this.basicStore.setComponentStore('wartung', { wartungen }, false)),
            tap(() => setTimeout(() => this.isLoading.set(false)))
          )
      ),
      cached()
    );

    this.filterStore.registerTable(this.wartungen$);
    this.filterStore.registerFilters(this.columnFilters);

    const token = this.authService.getToken();
    this.allowRepErstellen = getTokenRight(token.rights.program, 'AllowPREPCR');
    this.allowRepZurückspielen = getTokenRight(token.rights.program, 'AllowPREPSY');
  }

  @HostListener('window:beforeunload')
  ngOnDestroy() {
    this.colHideService.setColHide(this.columns, COL_NAME);
  }

  async columnHiddenChange() {
    if (!this.columns) return;
    const hides = await this.colHideService.columnHiddenChange(this.columns);
    this.hides.set(hides as string[]);
  }

  removeColFilter() {
    this.hides.set(this.colHideService.removeColHide(COL_NAME));
  }

  getExcelList() {
    const sortedCol = this.grid().columns.find(c => c.sortOrder != ClrDatagridSortOrder.UNSORTED);
    if (!sortedCol) {
      this.toastr.warning(
        this.mrTranslate.transform('Es muss mindestens eine Spalte sortiert sein bevor Sie diese Funktion nutzen können')
      );
      return;
    }

    if (sortedCol) {
      const colID = sortedCol.field;
      const colTyp = sortedCol.colType;
      const isDESC = sortedCol.sortOrder == ClrDatagridSortOrder.DESC;

      const sorted = this.filteredRows().sort((a, b) => {
        switch (colTyp) {
          case 'string':
            return a[colID]?.toString().localeCompare(b[colID]?.toString());
          case 'number':
            return +a[colID] - +b[colID];
        }
      });
      if (isDESC) sorted.reverse();
      this.excelService.exportAsExcelFile(sorted, this.mrTranslate.transform('Wartung'));
    } else {
      this.excelService.exportAsExcelFile(this.filteredRows(), this.mrTranslate.transform('Wartung'));
    }
  }

  protected setWartungMedia(node: WartungsPlanNode) {
    this.wartungsMedia.set(
      this.media.parseMediaPathWithTitle({
        title: `${node.maske} - ${this.localeDate.transform(node.letzteDatum)}`,
        filepath: node.RevBild,
      })
    );
  }

  protected openWartung(mode?: 'neu' | 'readonly') {
    this.overlayService.setOverlay({
      overlay: 'wartungsdialog',
      mode,
      objekt: this.selectedNode(),
      dataRefresh: this.dataRefresh,
    });
  }

  protected openVerwaltung() {
    this.overlayService.setOverlay({
      overlay: 'wartungsVerwaltung',
      dataRefresh: this.dataRefresh,
    });
  }

  protected toggleMulti() {
    this.isMultiMode.update(multi => !multi);
    if (!this.isMultiMode()) this.clearMulti();
  }

  protected clearMulti() {
    this.isMultiMode.set(false);
    this.selectedNodes.set([]);
  }

  protected async sendMultiWartung(multiModal: ClrModal) {
    const sendObj: MultiProtokollModel = {
      datum: this.datumModel() || BackendLocaleDatePipe.now,
      bemerkung: this.bemerkungModel(),
      objekte: this.selectedNodes(),
    };

    const { success, error } = await firstValueFrom(this.api.setMultiWartung(sendObj));

    if (success) {
      this.dataRefresh.next();
      multiModal.close();
      this.toastr.success(this.mrTranslate.transform('Erfolgreich gespeichert!'));
    } else {
      console.log(error);
      this.toastr.error(this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  protected async stopReplicate() {
    if (!this.allowRepZurückspielen) {
      this.toastr.warning(this.mrTranslate.transform('Für diese Aktion fehlen die Rechte'));
      return;
    }
    const text = this.mrTranslate.transform('Sind Sie sicher, dass Sie das Replikat zurückspielen wollen?');
    const ok = await this.confirmService.info(text);

    if (ok) this.isReplikatProgressOpen.set(true);
  }
}

export function findTermine(objekte: WartungsPlanNode[]): WartungsPlanNode[] {
  const today = dayjs().startOf('day');
  return objekte
    .map(o => {
      let einheit: 'd' | 'M' = o.einheit == 'Tag' ? 'd' : 'M';
      if (o.intervall <= 0) {
        o.intervall = 12;
        einheit = 'M';
      }
      let event_date = dayjs(o.intervallbeginn).startOf('day');

      if (o.letzteDatum) {
        const intervallDays = einheit == 'M' ? o.intervall * 30.5 : o.intervall;
        const deltaIntervall = Math.ceil(intervallDays * 0.05);
        const ldate = dayjs(o.letzteDatum).add(deltaIntervall, 'days');
        do {
          event_date = dayjs(event_date).add(o.intervall, einheit);
        } while (event_date <= ldate); // ? rechnen mit  floor(diff(heute - beginn, einheiten) / intervall)
      }

      if (event_date.day() == DAYS_OF_WEEK.SUNDAY) event_date = event_date.add(1, 'd');
      else if (event_date.day() == DAYS_OF_WEEK.SATURDAY) event_date = event_date.add(2, 'd');

      o.nextDatum = event_date.format(BackendLocaleDatePipe.format);
      o.diffFromNow = event_date.diff(today, 'days');
      return o;
    })
    .sort((a, b) => a.nextDatum.localeCompare(b.nextDatum));
}
